import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ArrowLeft, Edit, Save, X, Plus, Star, Calendar, Clock, AlertCircle, CheckCircle, TrendingUp, TrendingDown, Minus } from "lucide-react";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Decision, RatingEntry, useDecisions } from "@/hooks/useDecisions";

type ScheduledReview = Decision['scheduled_reviews'][number];

/** The subset of a decision this screen's form edits. */
interface DecisionEditForm {
  title: string;
  decision_date: string;
  rationale: string;
}
import { useFormatDate } from "../src/hooks/useFormatDate";
import { toast } from "sonner";
import { classifyReview } from "@/lib/review-classification";

interface DecisionDetailProps {
  decision: Decision;
  onBack: () => void;
}

const commonTags = ["product", "tech", "hiring", "design", "beta", "strategy", "marketing", "finance"];
const commonEmotions = ["excited", "nervous", "confident", "uncertain", "optimistic", "concerned", "hopeful", "anxious"];

export function DecisionDetail({ decision, onBack }: DecisionDetailProps) {
  const { updateDecision, deleteDecision, getCurrentRating, completeReview, loadMoreRatings, loadMoreReviews } = useDecisions();
  const { formatDate } = useFormatDate();
  const [isEditing, setIsEditing] = useState(false);
  const [tags, setTags] = useState<string[]>(decision?.tags?.filter(t => t && t.name).map(t => t.name) || []);
  const [newTag, setNewTag] = useState("");
  const [emotions, setEmotions] = useState<string[]>(decision?.emotions?.filter(e => e && e.name).map(e => e.name) || []);
  const [newEmotion, setNewEmotion] = useState("");
  const [reviewIntervals, setReviewIntervals] = useState<number[]>(decision?.review_intervals || []);
  const [day0Rating, setDay0Rating] = useState([decision?.day0_rating || 5]);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<{ day: number; id: string } | null>(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewOutcome, setReviewOutcome] = useState("");
  const [reviewLessons, setReviewLessons] = useState("");

  const [loadingMoreRatings, setLoadingMoreRatings] = useState(false);
  const [loadingMoreReviews, setLoadingMoreReviews] = useState(false);

  // Sync local state with decision prop changes
  useEffect(() => {
    if (decision) {
      setTags(decision.tags?.filter(t => t && t.name).map(t => t.name) || []);
      setEmotions(decision.emotions?.filter(e => e && e.name).map(e => e.name) || []);
      setReviewIntervals(decision.review_intervals || []);
      setDay0Rating([decision.day0_rating || 5]);
    }
  }, [decision]);

  const { register, handleSubmit, reset } = useForm<DecisionEditForm>({
    defaultValues: {
      title: decision?.title || "",
      decision_date: decision?.decision_date || "",
      rationale: decision?.rationale || ""
    }
  });

  const currentRating = decision ? getCurrentRating(decision) : 5;
  const ratingTrend = currentRating - (decision?.day0_rating || 5);

  // Prepare chart data - sort by day to ensure chronological order
  const chartData = decision?.rating_history
    ?.map((entry: RatingEntry) => ({
      day: entry.day,
      rating: entry.rating,
      date: new Date(entry.date).toLocaleDateString()
    }))
    .sort((a, b) => a.day - b.day) || [];

  const getRatingTrendIcon = () => {
    if (ratingTrend > 0.1) return TrendingUp;
    if (ratingTrend < -0.1) return TrendingDown;
    return Minus;
  };

  const getRatingTrendColor = () => {
    if (ratingTrend > 0.1) return "text-green-600";
    if (ratingTrend < -0.1) return "text-red-600";
    return "text-gray-500";
  };

  const TrendIcon = getRatingTrendIcon();
  const handleLoadOlderRatings = async () => {
    if (!decision?.id || loadingMoreRatings || !decision?.has_more_ratings) return;

    try {
      setLoadingMoreRatings(true);
      await loadMoreRatings(decision.id, decision.rating_history.length);
    } catch (error) {
      console.error('Failed to load older ratings:', error);
    } finally {
      setLoadingMoreRatings(false);
    }
  };

  const handleLoadOlderReviews = async () => {
    if (!decision?.id || loadingMoreReviews || !decision?.has_more_reviews) return;

    try {
      setLoadingMoreReviews(true);
      await loadMoreReviews(decision.id, decision.scheduled_reviews.length);
    } catch (error) {
      console.error('Failed to load older reviews:', error);
    } finally {
      setLoadingMoreReviews(false);
    }
  };


  const addTag = (tag: string) => {
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const addEmotion = (emotion: string) => {
    if (emotion && !emotions.includes(emotion)) {
      setEmotions([...emotions, emotion]);
      setNewEmotion("");
    }
  };

  const removeEmotion = (emotionToRemove: string) => {
    setEmotions(emotions.filter(emotion => emotion !== emotionToRemove));
  };

  const addReviewInterval = () => {
    setReviewIntervals([...reviewIntervals, 0]);
  };

  const updateReviewInterval = (index: number, value: number) => {
    const newIntervals = [...reviewIntervals];
    newIntervals[index] = value;
    setReviewIntervals(newIntervals);
  };

  const removeReviewInterval = (index: number) => {
    setReviewIntervals(reviewIntervals.filter((_, i) => i !== index));
  };
  
  // Use these functions to avoid TS unused warnings
  console.log({ addReviewInterval, updateReviewInterval, removeReviewInterval });

  const onSubmit = async (data: DecisionEditForm) => {
    if (!decision?.id) return;
    try {
      await updateDecision(decision.id, {
        title: data.title,
        decision_date: data.decision_date,
        rationale: data.rationale,
        day0_rating: day0Rating[0],
        review_intervals: reviewIntervals.filter(interval => interval > 0),
        tag_names: tags,
        emotion_names: emotions
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating decision:', error);
    }
  };

  const handleDelete = async () => {
    if (!decision?.id) return;
    try {
      await deleteDecision(decision.id);
      onBack();
    } catch (error) {
      console.error('Error deleting decision:', error);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setTags(decision?.tags?.filter(t => t && t.name).map(t => t.name) || []);
    setEmotions(decision?.emotions?.filter(e => e && e.name).map(e => e.name) || []);
    setReviewIntervals(decision?.review_intervals || []);
    setDay0Rating([decision?.day0_rating || 5]);
    reset();
  };

  const getReviewStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "text-green-600";
      case "overdue": return "text-red-600";
      case "upcoming": return "text-gray-500";
      case "due_now": return "text-blue-600";
      default: return "text-gray-500";
    }
  };

  const getReviewStatusIcon = (status: string) => {
    switch (status) {
      case "completed": return CheckCircle;
      case "overdue": return AlertCircle;
      case "upcoming": return Clock;
      case "due_now": return AlertCircle;
      default: return Clock;
    }
  };

  const handleCompleteReview = async () => {
    if (!selectedReview || !decision?.id) return;
    try {
      console.log('Completing review for day:', selectedReview.day);
      await completeReview(
        decision.id,
        selectedReview.day,
        reviewRating,
        reviewNotes || undefined,
        reviewOutcome || undefined,
        reviewLessons || undefined
      );
      setReviewDialogOpen(false);
      setSelectedReview(null);
      // Reset form
      setReviewRating(5);
      setReviewNotes("");
      setReviewOutcome("");
      setReviewLessons("");
      toast.success("Review completed successfully!");
    } catch (error) {
      console.error('Error completing review:', error);
      toast.error("Failed to complete review");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Decisions
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <Button onClick={() => setIsEditing(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Decision
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={cancelEdit}>
                  Cancel
                </Button>
                <Button form="decision-form" type="submit">
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </>
            )}
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Decision</AlertDialogTitle>
                   <AlertDialogDescription>
                     Are you sure you want to delete "{decision?.title || 'this decision'}"? This action cannot be undone.
                   </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <Tabs defaultValue="details" className="space-y-6">
          <TabsList>
            <TabsTrigger value="details">Decision Details</TabsTrigger>
            <TabsTrigger value="ratings">Rating History</TabsTrigger>
          </TabsList>

          <TabsContent value="details">
            <form id="decision-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Basic Information</CardTitle>
                      <CardDescription>Core details of the decision</CardDescription>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <Star className="h-5 w-5 text-yellow-500" />
                           <span className="text-xl font-bold">{currentRating}/10</span>
                            {(decision?.rating_history?.length || 0) > 1 && (
                             <>
                               <TrendIcon className={`h-4 w-4 ml-2 ${getRatingTrendColor()}`} />
                               <span className={`text-sm ${getRatingTrendColor()}`}>
                                 {ratingTrend > 0 ? `+${ratingTrend.toFixed(1)}` : ratingTrend.toFixed(1)}
                               </span>
                             </>
                           )}
                         </div>
                          <div className="text-sm text-muted-foreground">
                            {(decision?.rating_history?.length || 0) > 1 ? "Current confidence" : "Initial confidence"}
                          </div>
                       </div>
                        <div className="text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4 inline mr-1" />
                          Created {decision?.created_at ? formatDate(decision.created_at) : "Unknown"}
                          {decision?.updated_at && (
                            <div>Updated {formatDate(decision.updated_at)}</div>
                          )}
                        </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                   <div className="space-y-2">
                     <Label htmlFor="title">Decision Title</Label>
                     {isEditing ? (
                       <Input
                         id="title"
                         {...register("title", { required: "Decision title is required" })}
                       />
                       ) : (
                        <p className="text-lg font-medium">{decision?.title || ""}</p>
                      )}
                    </div>


                    <div className="space-y-2">
                      <Label htmlFor="decision_date">Date</Label>
                      {isEditing ? (
                        <Input
                          id="decision_date"
                          type="date"
                          {...register("decision_date", { required: "Date is required" })}
                        />
                      ) : (
                        <p>{decision?.decision_date ? formatDate(decision.decision_date) : ""}</p>
                      )}
                   </div>
                </CardContent>
              </Card>

              {/* Decision Details */}
               <Card>
                 <CardHeader>
                   <CardTitle>Decision Details</CardTitle>
                 </CardHeader>
                 <CardContent className="space-y-6">
                   <div className="grid gap-6 md:grid-cols-2">
                     {/* Left Column - Rationale and Context */}
                     <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="rationale">Rationale & Context</Label>
                          {isEditing ? (
                            <Textarea
                              id="rationale"
                              rows={6}
                              {...register("rationale", { required: "Rationale is required" })}
                            />
                           ) : (
                             <p className="whitespace-pre-wrap">{decision?.rationale || ""}</p>
                           )}
                         </div>
                     </div>

                     {/* Right Column - Tags and Emotions */}
                     <div className="space-y-4">
                       {/* Tags */}
                       <div className="space-y-2">
                         <Label>Tags</Label>
                         <div className="space-y-4">
                            <div className="flex flex-wrap gap-2">
                               {(isEditing ? tags : decision?.tags?.filter(t => t && t.name).map(t => t.name) || []).map((tag) => (
                                <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                                  {tag}
                                  {isEditing && (
                                    <button
                                      type="button"
                                      onClick={() => removeTag(tag)}
                                      className="ml-1 hover:text-destructive"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </Badge>
                              ))}
                               {(isEditing ? tags : decision?.tags || []).length === 0 && (
                                <span className="text-muted-foreground">No tags</span>
                              )}
                           </div>

                           {isEditing && (
                             <>
                               <div className="flex gap-2">
                                 <Input
                                   placeholder="Add a tag..."
                                   value={newTag}
                                   onChange={(e) => setNewTag(e.target.value)}
                                   onKeyPress={(e) => {
                                     if (e.key === "Enter") {
                                       e.preventDefault();
                                       addTag(newTag);
                                     }
                                   }}
                                 />
                                 <Button type="button" variant="outline" onClick={() => addTag(newTag)}>
                                   <Plus className="h-4 w-4" />
                                 </Button>
                               </div>

                               <div className="flex flex-wrap gap-2">
                                 {commonTags.map((tag) => (
                                   <Badge
                                     key={tag}
                                     variant="outline"
                                     className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                                     onClick={() => addTag(tag)}
                                   >
                                     {tag}
                                   </Badge>
                                 ))}
                               </div>
                             </>
                           )}
                         </div>
                       </div>

                       {/* Emotions */}
                       <div className="space-y-2">
                         <Label>Emotions</Label>
                         <div className="space-y-4">
                            <div className="flex flex-wrap gap-2">
                               {(isEditing ? emotions : decision?.emotions?.filter(e => e && e.name).map(e => e.name) || []).map((emotion) => (
                                <Badge key={emotion} variant="outline" className="flex items-center gap-1">
                                  {emotion}
                                  {isEditing && (
                                    <button
                                      type="button"
                                      onClick={() => removeEmotion(emotion)}
                                      className="ml-1 hover:text-destructive"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </Badge>
                              ))}
                               {(isEditing ? emotions : decision?.emotions || []).length === 0 && (
                                <span className="text-muted-foreground">No emotions recorded</span>
                              )}
                           </div>

                           {isEditing && (
                             <>
                               <div className="flex gap-2">
                                 <Input
                                   placeholder="Add an emotion..."
                                   value={newEmotion}
                                   onChange={(e) => setNewEmotion(e.target.value)}
                                   onKeyPress={(e) => {
                                     if (e.key === "Enter") {
                                       e.preventDefault();
                                       addEmotion(newEmotion);
                                     }
                                   }}
                                 />
                                 <Button type="button" variant="outline" onClick={() => addEmotion(newEmotion)}>
                                   <Plus className="h-4 w-4" />
                                 </Button>
                               </div>

                               <div className="flex flex-wrap gap-2">
                                 {commonEmotions.map((emotion) => (
                                   <Badge
                                     key={emotion}
                                     variant="outline"
                                     className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                                     onClick={() => addEmotion(emotion)}
                                   >
                                     {emotion}
                                   </Badge>
                                 ))}
                               </div>
                             </>
                           )}
                         </div>
                       </div>
                     </div>
                   </div>
                 </CardContent>
               </Card>

              {/* Review Schedule & History */}
              <Card>
                <CardHeader>
                  <CardTitle>Review Schedule & History</CardTitle>
                  <CardDescription>Track your decision reviews over time</CardDescription>
                </CardHeader>
                <CardContent>
                   <div className="space-y-4">
                     {(decision?.scheduled_reviews || []).map((review: ScheduledReview, index: number) => {
                       // scheduled_reviews.status is only reconciled by the
                       // review-maintenance task, so derive the display status
                       // from due_date and completed_at instead.
                       const reviewStatus = classifyReview(review);
                       const StatusIcon = getReviewStatusIcon(reviewStatus);
                       const ratingEntry = (decision?.rating_history || []).find((r: RatingEntry) => r.day === review.day);
                      
                      return (
                        <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <StatusIcon className={`h-5 w-5 ${getReviewStatusColor(reviewStatus)}`} />
                            <div>
                              <p className="font-medium">Day {review.day} Review</p>
                              <p className="text-sm text-muted-foreground">
                                {review.due_date ? `Due: ${formatDate(review.due_date)}` : "Not scheduled"}
                              </p>
                              {ratingEntry && reviewStatus === "completed" && (
                                <p className="text-sm text-blue-600">
                                  Rating: {ratingEntry.rating}/10
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                             <Badge 
                              variant={
                                reviewStatus === "completed"
                                  ? "default"
                                  : (reviewStatus === "overdue" || reviewStatus === "due_now")
                                    ? "destructive"
                                    : "secondary"
                              }
                             >
                               {reviewStatus}
                             </Badge>
                               {(reviewStatus === "overdue" || reviewStatus === "due_now") && (
                               <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                                 <DialogTrigger asChild>
                                   <Button 
                                     size="sm" 
                                     onClick={() => {
                                       console.log('Opening review dialog for day:', review.day);
                                       setSelectedReview({ day: review.day, id: review.id });
                                       setReviewRating(5);
                                       setReviewNotes("");
                                       setReviewOutcome("");
                                       setReviewLessons("");
                                       setReviewDialogOpen(true);
                                     }}
                                   >
                                     Complete Review
                                   </Button>
                                 </DialogTrigger>
                                 <DialogContent className="sm:max-w-[425px]">
                                   <DialogHeader>
                                     <DialogTitle>Complete Day {selectedReview?.day} Review</DialogTitle>
                                     <DialogDescription>
                                       Rate your confidence in this decision and add any notes.
                                     </DialogDescription>
                                   </DialogHeader>
                                   <div className="space-y-4 py-4">
                                     <div className="space-y-2">
                                       <Label htmlFor="rating">Rating (1-10)</Label>
                                       <Input
                                         id="rating"
                                         type="number"
                                         min="1"
                                         max="10"
                                         value={reviewRating}
                                         onChange={(e) => setReviewRating(parseInt(e.target.value))}
                                       />
                                     </div>
                                     <div className="space-y-2">
                                       <Label htmlFor="notes">Notes (optional)</Label>
                                       <Textarea
                                         id="notes"
                                         placeholder="Any thoughts or observations about this decision..."
                                         value={reviewNotes}
                                         onChange={(e) => setReviewNotes(e.target.value)}
                                       />
                                     </div>
                                     <div className="space-y-2">
                                       <Label htmlFor="outcome">Outcome (optional)</Label>
                                       <Textarea
                                         id="outcome"
                                         placeholder="What was the actual outcome of this decision?"
                                         value={reviewOutcome}
                                         onChange={(e) => setReviewOutcome(e.target.value)}
                                       />
                                     </div>
                                     <div className="space-y-2">
                                       <Label htmlFor="lessons">Lessons Learned (optional)</Label>
                                       <Textarea
                                         id="lessons"
                                         placeholder="What did you learn from this decision?"
                                         value={reviewLessons}
                                         onChange={(e) => setReviewLessons(e.target.value)}
                                       />
                                     </div>
                                   </div>
                                   <DialogFooter>
                                     <Button 
                                       variant="outline" 
                                       onClick={() => setReviewDialogOpen(false)}
                                     >
                                       Cancel
                                     </Button>
                                     <Button onClick={handleCompleteReview}>
                                       Complete Review
                                     </Button>
                                   </DialogFooter>
                                 </DialogContent>
                               </Dialog>
                             )}
                          </div>
                        </div>
                      );
                    })}

                    <div className="pt-2">
                      {decision?.has_more_reviews ? (
                        <Button variant="outline" onClick={handleLoadOlderReviews} disabled={loadingMoreReviews}>
                          {loadingMoreReviews ? "Loading..." : "Load older entries"}
                        </Button>
                      ) : (
                        <p className="text-sm text-muted-foreground">No older review history</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </form>
          </TabsContent>

          <TabsContent value="ratings">
            <div className="space-y-6">
              {/* Rating Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Rating Summary</CardTitle>
                  <CardDescription>How your confidence in this decision has changed over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3 mb-6">
                     <div className="text-center">
                       <div className="text-2xl font-bold">{decision?.day0_rating || 5}/10</div>
                       <div className="text-sm text-muted-foreground">Initial Rating (Day 0)</div>
                     </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{currentRating}/10</div>
                      <div className="text-sm text-muted-foreground">Current Rating</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getRatingTrendColor()}`}>
                        {ratingTrend > 0 ? `+${ratingTrend.toFixed(1)}` : ratingTrend.toFixed(1)}
                      </div>
                      <div className="text-sm text-muted-foreground">Change</div>
                    </div>
                  </div>

                   {(decision?.rating_history?.length || 0) > 1 && (
                     <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 50 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="day" 
                              label={{ value: 'Days', position: 'insideBottom', offset: -5 }}
                              allowDecimals={false}
                            />
                            <YAxis 
                              domain={[0, 10]}
                              label={{ value: 'Rating', angle: -90, position: 'insideLeft' }}
                              allowDecimals={false}
                            />
                            <Tooltip 
                              labelFormatter={(value) => `Day ${value}`}
                              formatter={(value) => [value, 'Rating']}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="rating" 
                              stroke="#8884d8" 
                              strokeWidth={3}
                              dot={{ fill: '#8884d8', strokeWidth: 2, r: 6 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                     </div>
                   )}
                </CardContent>
              </Card>

               {/* Rating History Details */}
               <Card>
                 <CardHeader>
                   <CardTitle>Detailed Rating History</CardTitle>
                   <CardDescription>All rating entries with due dates and completion information</CardDescription>
                 </CardHeader>
                 <CardContent>
                     <div className="space-y-4">
                       {(decision?.rating_history || []).map((entry: RatingEntry, index: number) => {
                         // Find the corresponding scheduled review for due date
                         const scheduledReview = (decision?.scheduled_reviews || []).find((r: ScheduledReview) => r.day === entry.day);
                         
                         return (
                           <div key={index} className="border rounded-lg p-4">
                             <div className="flex items-start justify-between mb-3">
                               <div className="space-y-1">
                                 <h4 className="font-medium">Day {entry.day} Review</h4>
                                 <div className="space-y-1 text-sm text-muted-foreground">
                                   {scheduledReview?.due_date && (
                                     <p>Due: {formatDate(scheduledReview.due_date)}</p>
                                   )}
                                   <p>Completed: {formatDate(entry.date)}</p>
                                 </div>
                               </div>
                               <div className="flex items-center gap-1">
                                 <Star className="h-4 w-4 text-yellow-500" />
                                 <span className="font-medium">{entry.rating}/10</span>
                               </div>
                             </div>
                            
                            {entry.notes && (
                              <div className="mb-3">
                                <p className="text-sm font-medium mb-1">Notes:</p>
                                <p className="text-sm text-muted-foreground">{entry.notes}</p>
                              </div>
                            )}
                            
                            {entry.outcome && (
                              <div className="mb-3">
                                <p className="text-sm font-medium mb-1">Outcome:</p>
                                <p className="text-sm text-muted-foreground">{entry.outcome}</p>
                              </div>
                            )}
                            
                            {entry.lessons && (
                              <div>
                                <p className="text-sm font-medium mb-1">Lessons Learned:</p>
                                <p className="text-sm text-muted-foreground">{entry.lessons}</p>
                              </div>
                            )}
                          </div>
                         );
                       })}

                     <div className="pt-2">
                       {decision?.has_more_ratings ? (
                         <Button variant="outline" onClick={handleLoadOlderRatings} disabled={loadingMoreRatings}>
                           {loadingMoreRatings ? "Loading..." : "Load older entries"}
                         </Button>
                       ) : (
                         <p className="text-sm text-muted-foreground">No older rating history</p>
                       )}
                     </div>
                   </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

        </Tabs>
    </div>
  );
}
